import clsx from "clsx";
import React, {
  FunctionComponent,
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

type TabPanelsProps = {
  children: ReactNode[];
  className?: string;
  contextRef?: MutableRefObject<TabPanelsContext | undefined>;
  defaultIndex: number;
  index?: number;
};

export type TabPanelsContext = {
  index: number;
  next: VoidFunction;
  prev: VoidFunction;
  setIndex: (index: number) => void;
};

const TabPanelsContext = createContext<TabPanelsContext>(
  {} as TabPanelsContext
);

export const useTabPanels = (): TabPanelsContext =>
  useContext(TabPanelsContext);

export const TabPanels: FunctionComponent<TabPanelsProps> = ({
  children,
  className,
  contextRef,
  defaultIndex,
  // index,
}) => {
  const [index, setIndex] = useState(defaultIndex);

  const next = useCallback(() => {
    const nextIndex = (index + 1) % children.length;
    setIndex(nextIndex);
  }, [children.length, index]);

  const prev = useCallback(() => {
    const prevIndex = (index - 1 + children.length) % children.length;
    setIndex(prevIndex);
  }, [children.length, index]);

  const ctx = { index, next, prev, setIndex };

  if (contextRef) {
    contextRef.current = ctx;
  }

  return (
    <TabPanelsContext.Provider value={ctx}>
      <div className={clsx("relative overflow-hidden transition", className)}>
        {children.map((child, key) => (
          <div
            className={clsx(
              "top-0 left-0 transition duration-500 w-full",
              index === key
                ? "translate-x-0 relative opacity-100"
                : "absolute opacity-0 pointer-events-none",
              index < key && "translate-x-[12rem]",
              index > key && "-translate-x-[12rem]"
            )}
            key={key}
          >
            {child}
          </div>
        ))}
      </div>
    </TabPanelsContext.Provider>
  );
};
